import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import "../assets/style/Carriers.css"
import { useTranslate, useJSONState, useStaticData } from '../hooks';
import {
  TextField, Grid, DataTable
  , Container, Group, Accordion, Centralizer, Button, Select, Switch, ChangeDialog, ConfirmDialog,
  Form,
  InputTable
} from '../components';
import { logger, deepCopyOf, areIdentical, dialog } from '../utils';
// import { carrierSuites } from '../utils/Variables/carrierSuites'
import { WriteToDeleteDialog } from "../components/Feedback"
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { number, z, ZodError } from "zod";
import { carrierSchema, CarriersuiteType, CarrierType } from '../types';

export default function Carriers() {
  const api = useAxiosPrivate();
  const [carrierSuites, setCarrierSuites] = useState<CarriersuiteType[]>([])
  const [carriersList, setCarriersList] = useState<CarrierType[]>([])
  const [originalCarrier, setOriginalCarrier] = useState<CarrierType>()
  const [carrier, setCProp, setCarrier] = useJSONState<CarrierType>({})
  const [carrierIdToDelete, setCarrierIdToDelete] = useState("");
  const location = useLocation()
  const { staticData } = useStaticData();

  const [changed, setChanged] = useState<boolean>(false) //Checks if form has changed
  const { t, tZod } = useTranslate();
  const navigate = useNavigate()
  const { carrierId } = useParams();
  const [validationErrors, setValidationErrors] = useState<ZodError<CarrierType> | null>(null)

  const newCarrierTempl: CarrierType = {
    carrierId: "new",
    name: "",
    carrierSuiteId: "",
    usePassiveFtp: false,
    pkgNoFromAPI: true,
    consNoFromAPI: true,
    consolidate: false,
    gaNo: "",
    ws_UserName: "",
    ws_Password: "",
    uNBaddressOwn: "",
    uNBqualifierOwn: "",
    uNBaddressRec: "",
    uNBqualifierRec: "",
    fTPserver: "",
    fTPaccount: "",
    fTPpassword: "",
    fTPport: 21,
    transferProtocol: "FTP",
    testMode: staticData?.warehouse.testMode ?? true,
    documents: [],
    numberSeries: {
      package: { from: "", current: "", to: "" },
      consignment: { from: "", current: "", to: "" }
    }
  }

  const getCarrierSuites = async () => {
    const response = await api.get({ endpoint: "/carrierSuites", mount: null })
    setCarrierSuites(response.data ?? [])
    console.log({ CarrierSuites: response.data })
  }

  const getCarrierList = async () => {
    const response = await api.get({ endpoint: "/carriers", mount: null })
    setCarriersList(response?.data ?? [])
  }

  const getCarrier = async (carrierId?: string) => {
    try {
      if (!carrierId) {
        setOriginalCarrier(undefined as unknown as CarrierType)
        setCarrier(undefined as unknown as CarrierType)
        return
      }
      if (carrierId === "new") {
        {
          setOriginalCarrier(newCarrierTempl)
          setCarrier(newCarrierTempl)
          return
        }
      }
      const response = await api.get({ endpoint: `carriers/${carrierId}`, mount: null })
      const og_Carrier = response.data

      if (og_Carrier == undefined) return navigate("/carriers", { replace: true })
      setOriginalCarrier(og_Carrier)
      setCarrier(deepCopyOf(og_Carrier))
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    getCarrierSuites()
    getCarrierList()
    if (location.state?.new) {
      setCarrier(newCarrierTempl)
      setOriginalCarrier(undefined as unknown as CarrierType)
    } else if (!carrierId && carrier) {
      setCarrier(undefined as unknown as CarrierType)
      setOriginalCarrier(undefined as unknown as CarrierType)
    }
  }, [location])

  useEffect(() => { carrierId && getCarrier(carrierId) }, [carrierId])

  useEffect(() => {
    if (validationErrors) setValidationErrors(null)
    if (areIdentical(carrier, originalCarrier)) {
      if (changed) setChanged(false)
    } else if (!changed) setChanged(true)
  }, [carrier, originalCarrier])

  const SaveCarrier = async () => {
    let requestBody = deepCopyOf(carrier)

    if (requestBody.consNoFromAPI) delete requestBody.numberSeries.consignment
    if (requestBody.pkgNoFromAPI) delete requestBody.numberSeries.package

    if (requestBody.carrierId == "") requestBody.carrierId = "new"
    try {
      const validation = carrierSchema.safeParse(requestBody)
      if (!validation.success) {
        /*
          For some fucking reason unionErrors does not exist on validation.error.issues[0] in Zods typescript.
          But it's in that array we got our errors...
        */
        try {
          let badZodError: any = validation.error.issues[0];
          
          if (!badZodError.unionErrors) {
            console.error(1, badZodError, { requestBody })
            setValidationErrors(tZod({ errors: [badZodError] } as any))
          }
          else {
            badZodError = badZodError?.unionErrors
              .filter((schema: any) => !schema.issues.some((i: any) => i.code === "invalid_literal"))
              ?.[0]
            console.error(2, badZodError, { requestBody })
            setValidationErrors(tZod(badZodError))
          }
        } catch (e) {
          console.error(e)
        }
        return Promise.resolve()
      }

      const response = await api.put({
        endpoint: `/carriers/${requestBody.carrierId}`,
        data: requestBody,
        mount: { loadingText: t("saving"), onLoadText: t("saved") }
      })

      if (requestBody.carrierId === "new")
        return navigate(`/carriers/${response.data.carrierId}`, { replace: true })

      setOriginalCarrier(carrier)
      getCarrierList()
    } catch (e) {
      console.error(e)
    }
  }

  const deleteCarrier = async () => {
    try {
      await api.delete({
        endpoint: `/carriers/${carrierIdToDelete}`,
        mount: { loadingText: t("deleting"), onLoadText: t("deleted") }
      })
      setCarrierIdToDelete("")
      getCarrierList();
      return navigate(`/carriers`, { replace: true })
    } catch (e) {
      console.error(e)

      if (e instanceof AxiosError) {
        const error = e.response?.data?.message || e.response?.data || e.message
        toast.error(error)
      } else {
        toast.error('An error occurred')
      }
    }
  }

  const generateNeededFields = () => {
    if (!carrier.carrierSuiteId) return <></>
    const neededFields = carrierSuites.find(c => c.value === carrier.carrierSuiteId)?.neededFields ?? {};

    const generateGroupFields = (group: string) => {
      return <>{
        neededFields[group].map((nf, i: number) => {

          const { field, name, required } = nf

          if (field === "transferProtocol") {
            return <Select
              value={carrier[field]}
              required={required != "never"}
              data={[{ name: "SFTP", value: "SFTP" }, { name: "FTP", value: "FTP" }]}
              onChange={(v) => setCProp(v.target.value, field)}
              error={validationErrors?.errors.find(e => e.path[0] === field)?.message}
              label={name ?? t(field)}
            />
          }

          if (typeof carrier[field] === "string" || typeof carrier[field] === "number") {
            return <TextField
              value={carrier[field]?.toString() as string}
              _type={((/secret|password/i).test(field.toLowerCase())) ? "password" : "text"}
              autoComplete={"new-password"}
              required={required != "never"}
              key={group + i}
              onChange={(v) => setCProp(typeof carrier[field] === "number" ?  parseFloat(v.target.value) : v.target.value, nf.field)}
              error={validationErrors?.errors.find(e => e.path[0] === field)?.message}
            >{name ?? t(field)}</TextField>
          }

          if (typeof carrier[field] === "boolean") {
            return <Switch
              checked={carrier[field] as boolean ?? false}
              label={name ?? t(field)}
              onChange={(v) => setCProp(v.target.checked, nf.field)}
              key={group + i}
            />
          }
        })
      }</>
    }

    return <>{
      Object.keys(neededFields).map((nfGroup: any) => {
        if (nfGroup == "EDI" && carrier.carrierSuiteId === "STANDARD" && !carrier.documents?.some(d => d.document == "transport instructions" && d.active))
          return <></>
        if (nfGroup.length > 2)
          return <Accordion label={t(nfGroup)} defaultOpen={true} key={nfGroup}>
            <Container>
              <Grid>{generateGroupFields(nfGroup)}</Grid>
            </Container>
          </Accordion>
        else return <Container key={nfGroup}>
          <Grid>{generateGroupFields(nfGroup)}</Grid>
        </Container>
      })
    }</>
  }

  return (
    <div className='schipt-carriers'>
      {carrierIdToDelete && carrier ?
        <WriteToDeleteDialog
          title={t("dialogs.sureDeleteCarrier")}
          textToWrite={carrier.name}
          message={t("dialogs.repeatTextToDelete") + carrier.name}
          onCancel={() => setCarrierIdToDelete("")}
          onOk={() => { deleteCarrier() }}
        />
        : <></>
      }
      <Centralizer style={{ width: "100vw" }}>
        <Container style={{
          height: "40vh",
          maxHeight: "50vh",
          position: "static",
          width: "clamp(1000px, 80%, 80vw)"
        }}>
          {carrier && Object.keys(carrier).length > 0 ?
            <Form onSubmit={() => SaveCarrier()}>
              <Group>
                <Container>
                  <Grid style={{ marginTop: "20px" }}>
                    <Select
                      label={t("CarrierSuite")}
                      value={carrier.carrierSuiteId}
                      data={carrierSuites}
                      onChange={(e) => {
                        const carrierSuite = deepCopyOf(carrierSuites.find(cs => cs.value === e.currentTarget.value))

                        if (!carrierSuite) return setCProp(e.target.value, "carrierSuiteId")

                        const newCarrier = deepCopyOf(carrier)

                        newCarrier.carrierSuiteId = carrierSuite.value
                        newCarrier.documents = deepCopyOf(carrierSuite.documents)

                        setCarrier(newCarrier)
                      }}
                      required={true}
                      blankOption={true}
                      style={{ width: "15rem" }}
                    />
                    <TextField
                      required
                      value={carrier.name}
                      onChange={(v) => setCProp(v.target.value, "name")}
                    >{t("name")}</TextField>
                    <Switch
                      label={t("testMode")}
                      disabled={staticData?.warehouse.testMode ?? false}
                      checked={carrier.testMode}
                      onChange={(e) => setCProp(e.currentTarget.checked, "testMode")}
                    />
                  </Grid>
                </Container>

                {generateNeededFields()}

                <InputTable
                  data={carrier.documents}
                  headers={{
                    document: {
                      type: "select", readOnly: true, options: [
                        { name: t("transport label"), value: "transport label" },
                        { name: t("waybill"), value: "waybill" },
                        { name: t("IFTMIN"), value: "transport instructions" },
                      ]
                    },
                    active: { type: "checkbox" },
                    copies: { type: "number", min: 1, max: 20, required: true },
                    autoprint: { type: "checkbox" },
                  }}
                  setProp={(val, ...path) => {
                    const key = path[2].toString();
                    const i = path[1] as number;

                    if (key === "autoprint" && val) {
                      const d = deepCopyOf(carrier.documents[i])
                      d.active = true
                      d.autoprint = true
                      setCProp(d, "documents", i)
                      return
                    }

                    if (key === "autoprint" && val) {
                      const d = deepCopyOf(carrier.documents[i])
                      d.active = true
                      d.autoprint = true
                      setCProp(d, "documents", i)
                      return
                    }
                    else if (key === "active" && val === false) {
                      const d = deepCopyOf(carrier.documents[i])
                      d.active = false
                      d.autoprint = false
                      setCProp(d, "documents", i)
                      return
                    }

                    setCProp(val, ...path)
                  }}
                  propPath='documents'
                />

                {!carrierSuites.find(c => c.value === carrier.carrierSuiteId)?.allowConsignmentNumberSerie
                  && !carrierSuites.find(c => c.value === carrier.carrierSuiteId)?.allowPackageNumberSerie ? <></> :
                  <Accordion label={t("numberseries")} defaultOpen={true}>
                    {!carrierSuites.find(c => c.value === carrier.carrierSuiteId)?.allowConsignmentNumberSerie ? <></> :
                      <Group label={t("consignmentNumber")}>
                        <Switch label={t("ownConsignmentNumberserie")}
                          checked={!carrier.consNoFromAPI}
                          onChange={(v) => setCProp(!v.currentTarget.checked, "consNoFromAPI")}
                        />
                        <Container>
                          <Grid>
                            {carrier.consNoFromAPI ? <></> :
                              <>
                                <TextField value={carrier.numberSeries.consignment.from}
                                  onChange={(v) => setCProp(v.target.value, 'numberSeries', 'consignment', 'from')}
                                  onBlur={(v) => !carrier.numberSeries.consignment.current?.length && setCProp(v.target.value, 'numberSeries', 'consignment', 'current')}
                                  error={validationErrors?.errors.find(e => e.path[1] === 'consignment' && e.path[2] === "from")?.message}
                                >{t("from")}</TextField>
                                <TextField value={carrier.numberSeries.consignment.to}
                                  onChange={(v) => setCProp(v.target.value, 'numberSeries', 'consignment', 'to')}
                                  error={validationErrors?.errors.find(e => e.path[1] === 'consignment' && e.path[2] === "to")?.message}
                                >{t("to")}</TextField>
                                <TextField value={carrier.numberSeries.consignment.current}
                                  onChange={(v) => setCProp(v.target.value, 'numberSeries', 'consignment', 'current')}
                                  error={validationErrors?.errors.find(e => e.path[1] === 'consignment' && e.path[2] === "current")?.message}
                                >{t("current")}</TextField>
                                <TextField readOnly={true}
                                  value={Math.max(0, parseInt(carrier.numberSeries?.consignment?.to) - (parseInt(carrier.numberSeries?.consignment?.current) ?? 0))}
                                >{t("numbersLeft")}</TextField>
                              </>
                            }
                          </Grid>
                        </Container>
                      </Group>
                    }
                    {!carrierSuites.find(c => c.value === carrier.carrierSuiteId)?.allowPackageNumberSerie ? <></> :
                      <Group label={t("packageNumber")}>
                        <Switch label={t("ownPackageNumberserie")}
                          checked={!carrier.pkgNoFromAPI}
                          onChange={(v) => setCProp(!v.currentTarget.checked, "pkgNoFromAPI")}
                        />
                        <Container>
                          <Grid>
                            {carrier.pkgNoFromAPI ? <></> :
                              <>
                                <TextField value={carrier.numberSeries.package.from}
                                  onChange={(v) => setCProp(v.target.value, 'numberSeries', 'package', 'from')}
                                  onBlur={(v) => !carrier.numberSeries.package.current?.length && setCProp(v.target.value, 'numberSeries', 'package', 'current')}
                                  error={validationErrors?.errors.find(e => e.path[1] === 'package' && e.path[2] === "from")?.message}
                                >{t("from")}</TextField>
                                <TextField value={carrier.numberSeries.package.to}
                                  onChange={(v) => setCProp(v.target.value, 'numberSeries', 'package', 'to')}
                                  error={validationErrors?.errors.find(e => e.path[1] === 'package' && e.path[2] === "to")?.message}
                                >{t("to")}</TextField>
                                <TextField value={carrier.numberSeries.package.current}
                                  onChange={(v) => setCProp(v.target.value, 'numberSeries', 'package', 'current')}
                                  error={validationErrors?.errors.find(e => e.path[1] === 'package' && e.path[2] === "current")?.message}
                                >{t("current")}</TextField>
                                <TextField readOnly={true}
                                  value={parseInt(carrier.numberSeries.package.to) - (parseInt(carrier.numberSeries.package.current) ?? 0)}>{t("numbersLeft")}</TextField>
                              </>}
                          </Grid>
                        </Container>
                      </Group>
                    }
                  </Accordion>
                }

                {originalCarrier?.carrierId && originalCarrier?.carrierId !== "" ?
                  <Button variant='delete' _type='button' onClick={() => { setCarrierIdToDelete(originalCarrier.carrierId) }}>{t("deleteCarrier")}</Button>
                  : <></>
                }
              </Group>
              <ChangeDialog show={changed}
                undo={async () => {
                  // If "Changed" carrier is a "new" carrier, then remove it.
                  if (originalCarrier?.carrierId === "") {
                    setCarriersList((prevState: any[]) => {
                      const i: number = prevState.findIndex((c: any) => c.name === "")
                      prevState.splice(i)
                      return prevState;
                    })
                    setCarrier(newCarrierTempl)
                  }
                  else setCarrier(originalCarrier as any)
                  setTimeout(() => setChanged(false), 10)
                }}
              />
            </Form>
            :
            <></>
          }
          <Accordion label={t("carriers")} defaultOpen={true}>
            <Container style={{ maxHeight: "35rem", overflowY: "hidden" }}>
              <DataTable
                data={carriersList}
                style={{ maxHeight: "34rem" }}
                hideColumns={["carrierId", "consNoFromAPI", "pkgNoFromAPI"]}
                openCallback={async (i: number) => { navigate(`/carriers/${carriersList[i].carrierId}`, { replace: true }) }}
              />
            </Container>
            <Button variant="add" style={{ display: changed ? 'none' : 'inline-block' }} onClick={() => {
              navigate(`/carriers`, { replace: true, state: { new: true } })
            }}>{t("newCarrier")}</Button>
          </Accordion>
        </Container>
      </Centralizer>
    </div>
  );
}